import { MAX_IMPORT_BYTES } from './assetListCsvImport.js'

export function suggestCsvReportDate(fileName) {
  const stem = String(fileName || '').replace(/\.csv$/i, '')
  const months = 'january february march april may june july august september october november december'.split(' ')
  const namedDate = new RegExp(`(?:^|[^a-z0-9])(${months.join('|')})[ _-]+(\\d{1,2})[ ,_-]+(\\d{4})(?=$|[^a-z0-9])`, 'gi')
  const candidates = [...stem.matchAll(namedDate)].map(match => [
    Number(match[3]), months.indexOf(match[1].toLowerCase()) + 1, Number(match[2]),
  ])
  for (const match of stem.matchAll(/(?:^|[^a-z0-9])(\d{4})-(\d{2})-(\d{2})(?=$|[^a-z0-9])/gi)) {
    candidates.push(match.slice(1).map(Number))
  }
  // Never use locale-dependent Date.parse or a rolling calendar date as source
  // evidence. Multiple dates and numeric month/day ordering need operator input.
  if (candidates.length !== 1) return ''
  const [year, month, day] = candidates[0]
  const date = new Date(Date.UTC(year, month - 1, day))
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return ''
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// Preserve bytes, not decoded/re-serialized CSV: BOMs, line endings and every
// original column survive download. Text decoding is only for preview/parsing.
export async function readImportFile(file) {
  if (file.size > MAX_IMPORT_BYTES) throw new Error('Choose a CSV smaller than 1 MB.')
  const bytes = new Uint8Array(await file.arrayBuffer())
  return { fileName: file.name, originalBase64: encodeImportBytes(bytes), text: new TextDecoder().decode(bytes) }
}

// A paste has text, not original file bytes or a filename. Archive the submitted
// text as UTF-8 with an explicit synthetic name through the same history path.
export function readPastedCsv(text) {
  return { fileName: 'Pasted CSV.csv', originalBase64: encodeImportBytes(new TextEncoder().encode(text)), text }
}

function encodeImportBytes(bytes) {
  if (bytes.length > MAX_IMPORT_BYTES) throw new Error('Choose a CSV smaller than 1 MB.')
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192))
  }
  return btoa(binary)
}

export function originalCsvBytes(base64) {
  return Uint8Array.from(atob(base64), char => char.charCodeAt(0))
}

export function importMembershipDiff(before, after) {
  if (!Array.isArray(before) || !Array.isArray(after)) return null
  const oldIds = new Set(before.map(row => row.instrumentId))
  const newIds = new Set(after.map(row => row.instrumentId))
  return {
    added: after.filter(row => !oldIds.has(row.instrumentId)),
    kept: after.filter(row => oldIds.has(row.instrumentId)),
    removed: before.filter(row => !newIds.has(row.instrumentId)),
  }
}

export async function runArchivedAssetListImport({ source, archive, execute, onRow = () => {}, signal }) {
  const importId = await archive.begin(source)
  const results = source.rows.map(row => ({ ...row }))
  let failure
  try {
    if (!signal?.aborted) await execute(row => {
      const index = results.findIndex(item => item.id === row.id)
      if (index >= 0) results[index] = { ...row }
      onRow(row)
    })
  } catch (error) { failure = error }
  const saved = results.filter(row => ['added', 'existing'].includes(row.status)).length
  const incomplete = results.some(row => !['added', 'existing', 'duplicate'].includes(row.status))
  const outcome = signal?.aborted ? 'stopped'
    : failure || incomplete ? saved ? 'partial' : 'failed' : 'completed'
  let recorded
  try {
    // No abort signal: a settled in-flight membership write must still be
    // recorded on Stop/unmount. A killed tab leaves the durable started record.
    recorded = await archive.finish(importId, { outcome, rows: results, error: failure?.message || null })
  } catch {
    throw new Error(`${failure ? `${failure.message} ` : ''}The CSV is archived, but completion could not be confirmed. Saved changes may remain. Check Import history and the current list before retrying.`)
  }
  if (failure) throw failure
  return { importId, outcome: recorded?.outcome || outcome }
}
